import type { Deploy, ExpertChat } from '@/types/api';
import {
  CHAT_STATUS,
  CLIENT_CHAT_STATUS,
  COMPOSER_REASON,
  DEPLOY_STATUS,
  ENVIRONMENT,
  ENVIRONMENT_ORDER,
  clientStatusNote,
} from '@/lib/copy';
import { formatClock, formatDateTime, formatRemaining, shortSha } from '@/lib/format';
import { useNow } from '@/lib/hooks';
import { cn } from '@/lib/utils';
import { Fact, Panel, PanelBody, PanelHeader } from '@/components/ui/panel';
import { CopyValue } from '@/components/ui/copy-value';
import { StateChip } from '@/components/state-chip';
import { HiddenGlyph } from '@/components/visibility-band';
import { useStreamStore, type ConnectionPhase } from '@/stores/stream-store';

/* ---------------------------------------------------------------------------
   The gauge cluster.

   The same facts in the same order on every review, so a reviewer's eye learns
   once where the claim, the lease and the client's projected status live. All of
   them are read off the server's document rather than derived here — including
   the composer reason, which comes from the same predicate that produces the
   409.
--------------------------------------------------------------------------- */

const CONNECTION_COPY: Record<ConnectionPhase, { label: string; tone: 'ok' | 'active' | 'warn' | 'danger' }> = {
  connecting: { label: 'Connecting', tone: 'active' },
  open: { label: 'Live', tone: 'ok' },
  reconnecting: { label: 'Reconnecting', tone: 'warn' },
  closed: { label: 'Stream closed', tone: 'danger' },
};

export function ReviewFacts({ chat, className }: { chat: ExpertChat; className?: string }) {
  const claim = chat.claim ?? null;
  const now = useNow(Boolean(claim), 15_000);
  const connection = useStreamStore((s) => s.connection);
  const contextTokens = useStreamStore((s) => s.contextTokens) ?? chat.context_tokens ?? null;
  const composer = useStreamStore((s) => s.composer) ?? chat.composer;

  const conn = CONNECTION_COPY[connection];

  return (
    <Panel className={className}>
      <PanelHeader title="Facts" meta={`chat ${chat.id}`} />
      <PanelBody className="py-1">
        <Fact label="Project">
          <span className="text-ink">{chat.project_name ?? `project ${chat.project_id}`}</span>
        </Fact>

        <Fact label="Status">
          <StateChip size="sm" state={CHAT_STATUS[chat.status]} />
        </Fact>

        <Fact
          label="Client sees"
          title={chat.client_visible_status_note ?? clientStatusNote(chat.status, chat.client_visible_status)}
        >
          <span className="inline-flex items-center gap-1.5">
            <HiddenGlyph className="h-[9px] w-[9px] text-hidden" />
            {CLIENT_CHAT_STATUS[chat.client_visible_status]}
          </span>
        </Fact>

        <Fact label="Checked out">
          {claim ? (
            <span className={cn(claim.is_mine ? 'text-live' : 'text-ink-2')}>
              {claim.is_mine ? 'you' : claim.expert_display_name}
            </span>
          ) : (
            <span className="text-ink-3">unclaimed</span>
          )}
        </Fact>

        {claim ? (
          <Fact label="Lease" title={`Claimed ${formatDateTime(claim.claimed_at)}`}>
            <span className={cn(formatRemaining(claim.expires_at, now) === 'expired' ? 'text-warn' : 'text-ink-2')}>
              {formatRemaining(claim.expires_at, now)}
            </span>
          </Fact>
        ) : null}

        <Fact label="Composer" title={COMPOSER_REASON[composer.reason_code].detail}>
          <StateChip size="sm" state={COMPOSER_REASON[composer.reason_code]} />
        </Fact>

        <Fact label="Stream">
          <StateChip size="sm" state={{ label: conn.label, tone: conn.tone }} />
        </Fact>

        {contextTokens !== null ? (
          <Fact
            label="Context"
            title="Against the 1M ceiling. At the ceiling the composer locks for both roles and every promotion action stays available — freeze, never destroy."
          >
            <span className={cn(chat.context_exhausted ? 'text-warn' : 'text-ink-2')}>
              {contextTokens.toLocaleString()}
              {chat.context_exhausted ? ' · exhausted' : ''}
            </span>
          </Fact>
        ) : null}

        {chat.status_version !== undefined ? (
          <Fact label="Status version" title="The optimistic-concurrency counter on the chat's status.">
            <span className="text-ink-3">{chat.status_version}</span>
          </Fact>
        ) : null}
      </PanelBody>
    </Panel>
  );
}

/**
 * Deploy history.
 *
 * Grouped by environment in pipeline order, because that is the question a
 * reviewer actually asks: what is on pre-staging, what is on staging, what is on
 * production. `succeeded` here means all three post-checks passed — the deployed
 * commit matched the pushed SHA, the provider reported success, and the
 * platform's own HTTPS probe answered — which is why a preview link is only
 * offered on that state and never on provider status alone.
 */
export function DeployHistory({ chat, className }: { chat: ExpertChat; className?: string }) {
  const deploys = (chat.deploys ?? []).filter((deploy): deploy is Deploy => Boolean(deploy));

  return (
    <Panel className={className}>
      <PanelHeader title="Deploys" meta={deploys.length ? `${deploys.length}` : 'none'} />
      <PanelBody className="space-y-3">
        {deploys.length === 0 ? (
          <p className="text-xs leading-[17px] text-ink-2">
            No deploy has been recorded on this chat. A run reaches pre-staging only; staging and production are reached
            through a promotion.
          </p>
        ) : (
          ENVIRONMENT_ORDER.map((environment) => {
            const group = deploys
              .filter((deploy) => deploy.environment === environment)
              .sort((a, b) => b.id - a.id);
            if (group.length === 0) return null;

            return (
              <div key={environment}>
                <p className="eyebrow" title={ENVIRONMENT[environment].detail}>
                  {ENVIRONMENT[environment].label}
                </p>
                <ul className="m-0 mt-1 list-none divide-y divide-rule p-0">
                  {group.map((deploy) => (
                    <li key={deploy.id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 py-1">
                      <StateChip size="sm" state={DEPLOY_STATUS[deploy.status]} />
                      {deploy.repo_slug ? (
                        <span className="font-mono text-tiny text-ink-2">{deploy.repo_slug}</span>
                      ) : null}
                      {deploy.commit_sha ? (
                        <CopyValue
                          className="text-tiny"
                          value={deploy.commit_sha}
                          display={shortSha(deploy.commit_sha)}
                        />
                      ) : null}
                      <span className="ml-auto font-mono text-tiny tabular-nums text-ink-3">
                        {formatClock(deploy.finished_at ?? deploy.started_at)}
                      </span>
                      {deploy.url && deploy.status === 'succeeded' ? (
                        <a
                          href={deploy.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full truncate font-mono text-tiny text-action underline underline-offset-2 hover:text-action-hi"
                        >
                          {deploy.url}
                        </a>
                      ) : null}
                      {deploy.error_code ? (
                        <span className="w-full font-mono text-tiny text-danger">{deploy.error_code}</span>
                      ) : null}
                      {deploy.has_logs ? (
                        <span
                          className="w-full font-mono text-tiny text-ink-3"
                          title="Redacted build logs exist for this deploy. The client sees a neutral message for the same failure."
                        >
                          redacted build logs recorded
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })
        )}
      </PanelBody>
    </Panel>
  );
}
